/**
 * N2K Gateway–inspired instrument look helpers for dashboard widgets.
 * Visual language: dark cards, muted uppercase labels, glanceable values,
 * thick tank bars with status-aware fill colors.
 */

export const INSTRUMENT = {
  cardBg: '#0f172a',
  cardBorder: '#1e293b',
  cardHoverBorder: '#334155',
  label: '#94a3b8',
  value: '#f8fafc',
  muted: '#64748b',
  accent: '#2dd4bf',
  track: '#1e293b',
  healthy: '#22c55e',
  warning: '#eab308',
  caution: '#f59e0b',
  critical: '#ef4444',
  charge: '#facc15',
  discharge: '#4ade80',
};

export function isWasteOrHoldingTank(widget) {
  const path = String(widget?.data_path || widget?.dataPath || widget?.config?.dataPath || '').toLowerCase();
  const name = String(widget?.name || '').toLowerCase();
  const hay = `${path} ${name}`;
  return /waste|gray|grey|black\s*water|sewage|holding|bilge/.test(hay);
}

export function isBatteryOrSocPath(widget) {
  const path = String(widget?.data_path || widget?.dataPath || '').toLowerCase();
  const name = String(widget?.name || '').toLowerCase();
  return /battery|stateofcharge|soc|electrical/.test(`${path} ${name}`);
}

/** Normalize SignalK ratio (0–1) or percent into 0–100. */
export function toDisplayPercent(raw, unit, min = 0, max = 100) {
  const n = typeof raw === 'number' ? raw : parseFloat(raw);
  if (!Number.isFinite(n)) return null;
  let value = n;
  if ((unit === '%' || max <= 1) && value >= 0 && value <= 1) {
    value = value * 100;
  }
  const lo = min <= 1 && max <= 1 ? min * 100 : min;
  const hi = min <= 1 && max <= 1 ? max * 100 : max;
  const range = Math.max(hi - lo, 0.001);
  const pct = ((value - lo) / range) * 100;
  return Math.max(0, Math.min(100, pct));
}

/**
 * Fill color for tank/level bars.
 * Waste/holding: high = critical (red). Fresh/fuel/battery: low = critical.
 */
export function instrumentBarColor(percent, widget, preferredColor) {
  if (preferredColor && preferredColor !== '#3b82f6' && preferredColor !== '#2563eb') {
    return preferredColor;
  }
  const pct = Number(percent) || 0;
  if (isWasteOrHoldingTank(widget)) {
    if (pct >= 85) return INSTRUMENT.critical;
    if (pct >= 65) return INSTRUMENT.caution;
    if (pct >= 40) return INSTRUMENT.warning;
    return INSTRUMENT.healthy;
  }
  if (pct >= 70) return INSTRUMENT.healthy;
  if (pct >= 40) return INSTRUMENT.warning;
  if (pct >= 20) return INSTRUMENT.caution;
  return INSTRUMENT.critical;
}

export function formatCapacitySubtitle(percent, widget) {
  const cfg = widget?.config || widget?.widget_config || {};
  const capacity = parseFloat(cfg.capacity ?? cfg.capacityLiters ?? cfg.maxCapacity);
  if (!Number.isFinite(capacity) || capacity <= 0 || percent == null) return null;
  const used = Math.round((percent / 100) * capacity);
  const unit = cfg.capacityUnit || 'L';
  return `${used} of ${Math.round(capacity)} ${unit}`;
}
